/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React, { forwardRef, memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link, NavLink } from 'react-router-dom';

import { Icon, createIconComponent } from 'uikit/icons';

import styles from '../styles/uikitButtonsButton.module.scss';

const buttonIconSizes = {
  xsmall: {
    default: 12,
    variantIconOrLink: 18,
  },
  small: {
    default: 18,
  },
  medium: {
    default: 18,
    variantIconOrLink: 24,
  },
  large: {
    default: 24,
  },
};

const getIconSize = ({ buttonSize, iconSize, variant = 'default' }) => {
  if (iconSize) return iconSize;
  if (buttonSize) {
    if (
      (variant === 'link' || variant === 'icon') &&
      buttonIconSizes[buttonSize]?.variantIconOrLink
    ) {
      return buttonIconSizes[buttonSize].variantIconOrLink;
    }
    return variant === 'auto' ? null : buttonIconSizes[buttonSize]?.default;
  }
};

const Button = forwardRef(
  (
    {
      ariaControls,
      ariaExpanded,
      ariaLabel = '',
      children = null,
      className = '',
      color = 'primary',
      icon = '',
      iconClass = '',
      iconPosition = 'left',
      iconSize,
      id,
      isDisabled = false,
      isExternalLink = false,
      isMetadataHighlighted = false,
      isNavLink = false,
      isSelected = false,
      isTypeAi = false,
      metadata = '',
      onClick = null,
      size = 'medium',
      testId = '',
      text = '',
      to = '',
      variant = 'default',
      ...other
    },
    ref,
  ) => {
    const CLASSES = {
      variant: {
        default: styles.isVariantDefault,
        ghost: styles.isVariantGhost,
        link: styles.isVariantLink,
        outlined: styles.isVariantOutlined,
        toggleIcon: styles.isVariantToggleIcon,
        toggleText: styles.isVariantToggleText,
        soft: styles.isVariantSoft,
        // DEPRECATED
        text: styles.isVariantGhost,
        // DEPRECATED
      },
      size: {
        auto: styles.isSizeAuto,
        xsmall: styles.isSizeXSmall,
        small: styles.isSizeSmall,
        medium: styles.isSizeMedium,
        large: styles.isSizeLarge,
      },
      color: {
        default: styles.isColorDefault,
        primary: styles.isColorPrimary,
        secondary: styles.isColorSecondary,
        brand: styles.isColorBrand,
        negative: styles.isColorNegative,
      },
    };

    const hasIcon = icon || isTypeAi;
    const isIconButton = children === null && !text && hasIcon;

    const classNames = classnames(
      styles.element,
      {
        [CLASSES.size[size]]: size,
        [CLASSES.variant[variant]]: variant,
        // This is set separately so we can use our different variants
        // with icon buttons (which have unique styles)
        [styles.isVariantIcon]: isIconButton,
        [CLASSES.color[color]]: color,
        [styles.isIconPositionLeft]: hasIcon && (iconPosition === 'left' || !iconPosition),
        [styles.isIconPositionRight]: hasIcon && iconPosition === 'right',
        [styles.isMetadataHighlighted]: isMetadataHighlighted,
        [styles.isSelected]: isSelected,
        [styles.isTypeAi]: isTypeAi,
      },
      className,
    );

    if (children === null && !text && !isTypeAi && !icon) {
      console.warn(
        'Please supply an icon prop when you choose an icon button! Icon buttons do not show text.',
      );
    }

    const isButtonElement = !to || isDisabled;

    const getElement = () => {
      if (isButtonElement) return 'button';
      if (isExternalLink) return 'a';

      return isNavLink ? NavLink : Link;
    };

    const getProps = () => {
      if (isButtonElement) return { disabled: isDisabled, onClick, type: 'button' };
      if (isExternalLink) return { href: to };

      return {
        ...(isNavLink && {
          className: ({ isActive }) => classnames(classNames, isActive && styles.isSelected),
          end: to === '.',
        }),
        to,
      };
    };

    const renderIcon = (position) => {
      if (!hasIcon) return null;

      const isCustomIcon = typeof icon === 'string';

      const positionClasses = {
        [styles.isPositionLeft]: (position === 'left' || (isTypeAi && !position)) && !isIconButton,
        [styles.isPositionRight]: position === 'right' && !isIconButton,
      };

      const iconProps = {
        className: classnames(styles.icon, positionClasses, iconClass),
        size: getIconSize({
          buttonSize: size,
          iconSize,
          variant: isIconButton ? 'icon' : variant,
        }),
        ...(isCustomIcon ? { testId: `${testId}-icon` } : { 'data-testid': `${testId}-icon` }),
        // iconSize is not applied to MUI Icon components
        // as the inline styles override the icon selectors in this component
      };

      if (isTypeAi) {
        return (
          <div className={classnames(styles.aiIcon, positionClasses)}>
            <Icon icon="ai" size={iconSize || 18} />
          </div>
        );
      }

      return createIconComponent({ icon, ...iconProps });
    };

    const Element = getElement();
    const buttonProps = getProps();

    return (
      <Element
        className={classNames}
        ref={ref}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        aria-label={ariaLabel}
        id={id}
        data-testid={testId}
        {...buttonProps}
        {...other}
      >
        {hasIcon && (iconPosition === 'left' || !iconPosition) && renderIcon('left')}
        {hasIcon && text ? <span className={styles.text}>{text}</span> : text}
        {children}
        {(metadata ?? '') !== '' && <span className={styles.metadata}>{metadata}</span>}
        {hasIcon && iconPosition === 'right' && renderIcon('right')}
      </Element>
    );
  },
);

Button.displayName = 'Button';

Button.propTypes = {
  ariaControls: PropTypes.string,
  ariaExpanded: PropTypes.bool,
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'secondary', 'brand', 'negative']),
  onClick: PropTypes.func,
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element, // for including MUI Icon React components
  ]),
  iconClass: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  iconSize: PropTypes.number,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isDisabled: PropTypes.bool,
  isExternalLink: PropTypes.bool,
  isMetadataHighlighted: PropTypes.bool,
  isNavLink: PropTypes.bool,
  isSelected: PropTypes.bool,
  isTypeAi: PropTypes.bool,
  metadata: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.oneOf(['auto', 'xsmall', 'small', 'medium', 'large']),
  testId: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  to: PropTypes.string,
  variant: PropTypes.oneOf([
    'default',
    'ghost',
    'link',
    'outlined',
    'toggleIcon',
    'toggleText',
    'soft',
    // DEPRECATED
    'text',
    // DEPRECATED
  ]),
};

export { Button as ButtonStorybook };
export default memo(Button);
